import { Report } from "./Report";
import { Asset } from "./Asset";
import { TaskPhase } from "./TaskPhase";
import { estateApi } from "../apis";
import { ProbateType } from "./ProbateType";
import { EstateSummary } from "./EstateSummary";
import { AdvisorGroup, AdvisorGroupJSON } from "./AdvisorGroup";
import { EstateUserInfo, EstateUserInfoJson } from "./EstateUserInfo";

// database field names
export type EstateJSON = {
  id?: number;
  // relationship?: string;
  // relationship_more?: string;
  // advisory_position?: string;
  // advisory_position_more?: string;
  user_id?: number;
  advisor_group_id?: number;
  // charge_id: "fake_charge_id"
  deceased_first_name?: string;
  deceased_middle_name?: string;
  deceased_last_name?: string;
  deceased_suffix?: string;
  estate_id?: number; // de-conflict id with estate_id
  executor_relationship_status?: "Great";
  has_business_ownerships?: boolean;
  has_investment_accounts?: boolean;
  has_life_insurance?: boolean;
  has_out_of_state_assets?: boolean; // convert to boolean
  has_passed?: string; // convert to boolean
  has_real_estate?: boolean;
  has_trust?: boolean;
  has_will?: string;
  leave_minors?: "YES"; // convert to boolean
  leave_pets?: "YES"; // convert to boolean
  passed_on?: string; // convert to date
  was_veteran?: string; // convert to boolean
  avatar_key?: string;
  executor_first?: string;
  executor_middle?: string;
  executor_last?: string;
  executor_suffix?: string;
  executor_email?: string;
  executor_phone?: string;
  address?: string;
  country_id?: number;
  state_id?: number;
  property_ownership?: string;
  property_value?: number;
  letters?: string;
  tasks_completed?: string; // json string to be converted to string[]
  type_of_assets?: string; // json string to be converted to string[]
  estate_team?: string; // json string to be converted to string[]
  need_help_with?: string; // json string to be converted to string[]
  need_help_with_something_else?: string;
  county?: string;
  value_below_threshold?: string;
  // declared_role?: string;
  already_started?: boolean;
  probate_type_id?: number | null;
  probate_type?: ProbateType;
  latest_activity?: string;
  users_profile_id?: number;
  deleted_at?: string;
  is_demo?: boolean;
  primary_home_complete?: boolean;
  advisor_group?: AdvisorGroupJSON;
  estate_user_info?: EstateUserInfoJson;
};

export interface NameValues {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
}

export class Estate {
  static path = "/estates";
  static avatarPath = "/avatar";
  path = "/estates";

  constructor(
    public id?: number,
    public userId?: number,
    public userProfileId?: number,
    public deceasedFirstName?: string,
    public deceasedMiddleName?: string,
    public deceasedLastName?: string,
    public deceasedSuffix?: string,
    // public relationship?: string, // = "child",
    // public relationshipMore?: string,
    public hasWill?: string,
    public propertyOwnership?: string,
    public propertyValue?: number,
    public letters?: string,
    public passedOn?: string,
    public avatarKey?: string,
    public executorFirst?: string,
    public executorMiddle?: string,
    public executorLast?: string,
    public executorSuffix?: string,
    public executorEmail?: string,
    public executorPhone?: string,
    public address?: string, // full address string: "1423 Elrondo Village Drive, San Diego, CA 92109"
    public countryId?: number,
    public stateId?: number,
    public county?: string,
    public valueBelowThreshold?: string,
    // public declaredRole?: string,
    public hasPassed?: boolean,
    public alreadyStarted?: boolean,
    public advisorGroupId?: number,
    public advisorGroup?: AdvisorGroup,
    public estateUserInfo?: EstateUserInfo,
    // public advisorGroupName?: string,
    // public numRelationships?: number,
    // public fairMarketValue?: number,
    // NOTE: opportunities are assets of a certain type?
    public assets?: Asset[] | null,
    // public opportunities?: Opportunity[],
    // public notes?: EstateNote[] | null,
    public reports?: Report[] | null,
    public taskPhases?: TaskPhase[] | null,
    public estateSummary?: EstateSummary | null,
    public tasksCompleted?: string[],
    public typeOfAssets?: string[],
    public estateTeam?: string[],
    public needHelpWith?: string[],
    public needHelpWithSomeThingElse?: string,
    public probateType?: ProbateType,
    public updatedAt?: string,
    public deletedAt?: string,
    public isDemo?: boolean,
    public wasVeteran?: string,
    public primaryHomeComplete?: boolean,
    public hasRealEstate?: boolean, // support legacy
    public hasInvestmentAccounts?: boolean, // support legacy
    public hasLifeInsurance?: boolean, // support legacy
    public hasBusinesses?: boolean, // support legacy
    public hasOutOfStateAssets?: boolean, // support legacy
    public hasTrust?: boolean // support legacy
  ) {}

  // clone
  static from(estate: Estate): Estate {
    return new Estate(
      estate.id,
      estate.userId,
      estate.userProfileId,
      estate.deceasedFirstName,
      estate.deceasedMiddleName,
      estate.deceasedLastName,
      estate.deceasedSuffix,
      // estate.relationship, // = "child",
      // estate.relationshipMore,
      estate.hasWill,
      estate.propertyOwnership,
      estate.propertyValue,
      estate.letters,
      estate.passedOn,
      estate.avatarKey,
      estate.executorFirst,
      estate.executorMiddle,
      estate.executorLast,
      estate.executorSuffix,
      estate.executorEmail,
      estate.executorPhone,
      estate.address,
      estate.countryId,
      estate.stateId,
      estate.county,
      estate.valueBelowThreshold,
      // estate.declaredRole,
      estate.hasPassed,
      estate.alreadyStarted,
      estate.advisorGroupId,
      estate.advisorGroup,
      estate.estateUserInfo,
      // estate.numRelationships,
      // estate.fairMarketValue,
      // NOTE: opportunities are assets of a certain type?
      estate.assets,
      // estate.opportunities?: Opportunity[],
      // estate.notes?: EstateNote[] | null,
      estate.reports,
      estate.taskPhases,
      estate.estateSummary,
      estate.tasksCompleted,
      estate.typeOfAssets,
      estate.estateTeam,
      estate.needHelpWith,
      estate.needHelpWithSomeThingElse,
      estate.probateType,
      estate.updatedAt,
      estate.deletedAt,
      estate.isDemo,
      estate.wasVeteran,
      estate.primaryHomeComplete,
      estate.hasRealEstate,
      estate.hasInvestmentAccounts,
      estate.hasLifeInsurance,
      estate.hasBusinesses,
      estate.hasOutOfStateAssets,
      estate.hasTrust
    );
  }

  static fromJSON = (json: EstateJSON): Estate => {
    return new Estate(
      json.id,
      json.user_id,
      json.users_profile_id,
      json.deceased_first_name,
      json.deceased_middle_name,
      json.deceased_last_name,
      json.deceased_suffix,
      // json.relationship,
      // json.relationship_more,
      json.has_will,
      json.property_ownership,
      json.property_value,
      json.letters,
      json.passed_on,
      json.avatar_key,
      json.executor_first,
      json.executor_middle,
      json.executor_last,
      json.executor_suffix,
      json.executor_email,
      json.executor_phone,
      json.address,
      json.country_id,
      json.state_id,
      json.county,
      json.value_below_threshold,
      // json.declared_role,
      json.has_passed === "1" || (typeof json.has_passed === "string" && json.has_passed.toLowerCase() === "yes"),
      json.already_started,
      json.advisor_group_id,
      json.advisor_group
        ? AdvisorGroup.fromJSON(json.advisor_group)
        : undefined,
      json.estate_user_info
        ? EstateUserInfo.fromJSON(json.estate_user_info)
        : undefined,
      // undefined,
      undefined,
      undefined,
      // undefined,
      undefined,
      undefined,
      json.tasks_completed ? JSON.parse(json.tasks_completed) : undefined,
      json.type_of_assets ? JSON.parse(json.type_of_assets) : undefined,
      json.estate_team ? JSON.parse(json.estate_team) : undefined,
      json.need_help_with ? JSON.parse(json.need_help_with) : undefined,
      json.need_help_with_something_else,
      json.probate_type ? ProbateType.fromJSON(json.probate_type) : undefined,
      json.latest_activity,
      json.deleted_at,
      json.is_demo,
      json.was_veteran,
      json.primary_home_complete,
      json.has_real_estate,
      json.has_investment_accounts,
      json.has_investment_accounts,
      json.has_business_ownerships,
      json.has_out_of_state_assets,
      json.has_trust
    );
  };

  toJson(): EstateJSON {
    const json: any = {};
    this.alreadyStarted !== undefined &&
      (json.already_started = this.alreadyStarted);
    this.hasPassed !== undefined && (json.has_passed = this.hasPassed);
    this.deceasedFirstName &&
      (json.deceased_first_name = this.deceasedFirstName);
    this.deceasedMiddleName &&
      (json.deceased_middle_name = this.deceasedMiddleName);
    this.deceasedLastName && (json.deceased_last_name = this.deceasedLastName);
    this.deceasedSuffix && (json.deceased_suffix = this.deceasedSuffix);
    // this.relationship && (json.relationship = this.relationship);
    // this.relationshipMore && (json.relationship_more = this.relationshipMore);
    this.address && (json.address = this.address);
    this.countryId && (json.country_id = this.countryId);
    this.stateId && (json.state_id = this.stateId);
    this.county && (json.county = this.county);
    
    this.passedOn && (json.passed_on = this.passedOn);
    if(this.passedOn === "") {
      json.passed_on = null
    }

    this.hasWill && (json.has_will = this.hasWill);
    this.propertyOwnership &&
      (json.property_ownership = this.propertyOwnership);
    this.propertyValue && (json.property_value = this.propertyValue);
    this.letters && (json.letters = this.letters);
    this.valueBelowThreshold &&
      (json.value_below_threshold = this.valueBelowThreshold);
    // this.declaredRole && (json.declared_role = this.declaredRole);
    this.tasksCompleted &&
      (json.tasks_completed = JSON.stringify(this.tasksCompleted));
    this.typeOfAssets &&
      (json.type_of_assets = JSON.stringify(this.typeOfAssets));
    this.estateTeam && (json.estate_team = JSON.stringify(this.estateTeam));
    this.needHelpWith &&
      (json.need_help_with = JSON.stringify(this.needHelpWith));
    // json.need_help_with_something_else = this.needHelpWithSomeThingElse;
    this.probateType?.id && json.need_help_with;
    this.probateType && this.probateType.toJSON();
    // this.stateProbateTypeId &&
    //   (json.state_probate_types_id = JSON.stringify(this.stateProbateTypeId));
    this.wasVeteran && (json.was_veteran = this.wasVeteran);
    json.primary_home_complete = this.primaryHomeComplete;
    json.has_real_estate = this.hasRealEstate;
    json.has_investment_accounts = this.hasInvestmentAccounts;
    json.has_life_insurance = this.hasLifeInsurance;
    json.has_business_ownerships = this.hasBusinesses;
    json.has_out_of_state_assets = this.hasOutOfStateAssets;
    json.has_trust = this.hasTrust;
    json.advisor_group = this.advisorGroup?.toJSON();
    json.estate_user_info = this.estateUserInfo?.toJSON();
    json.executor_email = this.executorEmail
    json.executor_phone = this.executorPhone
    json.executor_first = this.executorFirst
    json.executor_middle = this.executorMiddle
    json.executor_last = this.executorLast
    json.executor_suffix = this.executorSuffix
    return json;
  }

  get nameValues(): NameValues {
    return {
      firstName: this?.deceasedFirstName || "",
      middleName: this?.deceasedMiddleName || "",
      lastName: this?.deceasedLastName || "",
      suffix: this?.deceasedSuffix || "",
    };
  }

  async saveEstate(): Promise<Estate> {
    if (this?.id) {
      const fields = this.toJson();
      await estateApi.updateEstate(this.id, fields);
    } else {
      this.id = await estateApi.createEstate(this.toJson());
    }
    return this;
  }

  get primaryRealEstate(): Asset | undefined {
    let asset = undefined;
    if (this.assets) {
      const reAssets = this.assets?.filter(
        (asset: Asset) => asset.category?.category === "Real Estate"
      );
      const primaryHomeIndex = reAssets?.findIndex(
        (asset: Asset) => asset.subcategory?.subcategory === "Primary Home"
      );
      if (primaryHomeIndex > 0) {
        asset = reAssets[primaryHomeIndex];
      } else {
        // return the first real estate asset
        asset = reAssets[0];
      }
    }
    return asset;
  }

  get name(): string | undefined {
    let retName = this.deceasedFirstName;
    if (this.deceasedMiddleName) {
      retName += ` ${this.deceasedMiddleName}`;
    }
    if (this.deceasedLastName) {
      retName += ` ${this.deceasedLastName}`;
    }
    if (this.deceasedSuffix) {
      retName += `, ${this.deceasedSuffix}`;
    }
    return retName;
  }
}
